/** Skema validasi bersama FE & BE. Satu sumber kebenaran. */
import { z } from 'zod';
import {
  INDIVIDUAL_STATUSES,
  INTENSIVE_CLASS_STATUSES,
  REGION_LEVELS,
  ROLES,
  TBM_STATUSES,
} from './types.js';

export const loginSchema = z.object({
  identifier: z.string().min(1, 'Email atau username wajib diisi'),
  password: z.string().min(1, 'Kata sandi wajib diisi'),
  remember: z.boolean().optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Kata sandi saat ini wajib diisi'),
  newPassword: z.string().min(8, 'Kata sandi baru minimal 8 karakter'),
});

export const userSchema = z.object({
  name: z.string().min(1, 'Nama wajib diisi'),
  email: z.string().email('Format email tidak valid').optional().nullable(),
  username: z.string().min(3, 'Username minimal 3 karakter'),
  phone: z.string().optional().nullable(),
  role: z.enum(ROLES),
  individualId: z.string().optional().nullable(),
  password: z.string().min(6, 'Kata sandi minimal 6 karakter').optional(),
  isActive: z.boolean().optional(),
});
export type UserInput = z.infer<typeof userSchema>;

export const portfolioSchema = z.object({
  individualId: z.string().min(1, 'ID Individu wajib diisi'),
  category: z.enum(['prestasi', 'karya', 'publikasi']),
  title: z.string().min(1, 'Judul wajib diisi'),
  description: z.string().optional().nullable(),
  dateOrYear: z.string().optional().nullable(),
  linkUrl: z.string().optional().nullable(),
  fileKey: z.string().optional().nullable(),
  fileName: z.string().optional().nullable(),
  fileSizeBytes: z.number().optional().nullable(),
});
export type PortfolioInput = z.infer<typeof portfolioSchema>;

export const regionSchema = z.object({
  parentId: z.string().nullable().optional(),
  level: z.enum(REGION_LEVELS),
  name: z.string().min(1, 'Nama wilayah wajib diisi'),
  code: z.string().optional().nullable(),
});

/**
 * `code` tidak ada di skema input mana pun — nilainya digenerate server saat
 * data dibuat dan tidak pernah diterima dari klien. Kalau ia boleh dikirim,
 * klien bisa menabrak kode milik data lain dan urutannya jadi berlubang.
 */
export const tbmSchema = z.object({
  name: z.string().min(1, 'Nama TBM wajib diisi'),
  regionId: z.string().min(1, 'Desa wajib dipilih'),
  angkatan: z.string().nullable().optional(),
  joinedPeriod: z.string().nullable().optional(),
  socialMedia: z.string().nullable().optional(),
  photoKey: z.string().nullable().optional(),
  status: z.enum(TBM_STATUSES).optional(),
});
export type TbmInput = z.infer<typeof tbmSchema>;

export const individualSchema = z.object({
  name: z.string().min(1, 'Nama wajib diisi'),
  gender: z.enum(['L', 'P']).nullable().optional(),
  birthPlace: z.string().nullable().optional(),
  birthDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Format tanggal harus YYYY-MM-DD')
    .nullable()
    .optional()
    .or(z.literal('')),
  phone: z.string().nullable().optional(),
  email: z.string().email('Format email tidak valid').nullable().optional().or(z.literal('')),
  photoKey: z.string().nullable().optional(),
  personalBranding: z.string().nullable().optional(),
  profession: z.string().nullable().optional(),
  lastEducation: z.enum(['SMA', 'D3', 'S1', 'S2']).nullable().optional().or(z.literal('')),
  status: z.enum(INDIVIDUAL_STATUSES).optional(),
  /**
   * Boleh kosong. Saat TBM dihapus, anggotanya TIDAK ikut terhapus — kolom ini
   * yang dikosongkan, dan orangnya menunggu dipasangkan ke TBM lain.
   */
  tbmId: z.string().nullable().optional(),
});
export type IndividualInput = z.infer<typeof individualSchema>;

/* ── Instrumen (Fase 3) ───────────────────────────────────────────────── */

export const dimensionSchema = z.object({
  code: z.string().min(1, 'Kode dimensi wajib diisi'),
  name: z.string().min(1, 'Nama dimensi wajib diisi'),
  journey: z.string().nullable().optional(),
});
export type DimensionInput = z.infer<typeof dimensionSchema>;

/**
 * Kriteria dan MoV ikut dalam satu muatan indikator, bukan endpoint terpisah.
 *
 * Empat kriteria itu bukan data mandiri — mereka hanya bermakna sebagai satu
 * himpunan utuh milik indikatornya. Memisahnya jadi endpoint sendiri membuka
 * kemungkinan indikator tersimpan dengan kriteria separuh, keadaan yang tidak
 * pernah sah namun harus ditangani di mana-mana.
 */
export const indicatorSchema = z.object({
  number: z.number().int().min(1, 'Nomor indikator wajib diisi'),
  name: z.string().min(1, 'Nama indikator wajib diisi'),
  description: z.string().nullable().optional(),
  weight: z.number().min(0, 'Bobot tidak boleh negatif').max(100, 'Bobot maksimal 100'),
  priorityYear: z.number().int().min(1).max(3).nullable().optional(),
  criteria: z
    .array(
      z.object({
        score: z.number().int().min(1).max(4),
        description: z.string(),
      }),
    )
    .max(4),
  mov: z
    .array(
      z.object({
        label: z.string().min(1),
        requirement: z.enum(['optional', 'required']).default('optional'),
      }),
    )
    .max(20),
});
export type IndicatorInput = z.infer<typeof indicatorSchema>;

/**
 * Ambang kategori. `gt` = "indeks LEBIH DARI angka ini".
 *
 * Disimpan sebagai batas-bawah-terbuka, bukan rentang {min,max}, karena
 * indeks bernilai desimal. Band "71–85" yang dibaca sebagai rentang akan
 * menolak 70,4; ditulis sebagai gt:70 ia menerimanya — dan itulah yang benar
 * menurut formula Excel klien. Rentang tampilan diturunkan dari gt, bukan
 * sebaliknya, supaya keduanya tidak bisa berselisih.
 */
export const bandsSchema = z.object({
  bands: z
    .array(
      z.object({
        gt: z.number().nullable(),
        name: z.string().min(1, 'Nama kategori wajib diisi'),
        nameIndividual: z.string().nullable().optional(),
        characteristic: z.string().nullable().optional(),
      }),
    )
    .min(1, 'Minimal satu kategori'),
});
export type BandsInput = z.infer<typeof bandsSchema>;

export const listQuerySchema = z.object({
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(20),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

export const intensiveClassSessionSchema = z.object({
  sessionNumber: z.number().int().min(1).max(50),
  topic: z.string().min(1, 'Tema sesi wajib diisi'),
  speaker: z.string().min(1, 'Nama narasumber/pembicara wajib diisi'),
  description: z.string().optional().nullable(),
  date: z.string().optional().nullable(),
});
export type IntensiveClassSessionInput = z.infer<typeof intensiveClassSessionSchema>;

export const intensiveSessionScoreSchema = z.object({
  sessionNumber: z.number().int().min(1).max(50),
  preTest: z.number().min(0).max(100).optional().nullable(),
  postTest: z.number().min(0).max(100).optional().nullable(),
  gain: z.number().optional().nullable(),
});
export type IntensiveSessionScoreInput = z.infer<typeof intensiveSessionScoreSchema>;

export const individualIntensiveClassSchema = z.object({
  individualId: z.string().min(1, 'Individu wajib dipilih'),
  attendancePct: z.number().min(0).max(100, 'Persentase presensi antara 0 s.d. 100'),
  attendedSessions: z.array(z.number().int().min(1).max(50)).optional().nullable(),
  sessionScores: z.array(intensiveSessionScoreSchema).optional().nullable(),
  preTestScore: z.number().min(0).max(100).optional().nullable(),
  postTestScore: z.number().min(0).max(100).optional().nullable(),
  gainScore: z.number().optional().nullable(),
  status: z.enum(INTENSIVE_CLASS_STATUSES).default('mengikuti'),
  certificateKey: z.string().optional().nullable(),
  certificateFileName: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});
export type IndividualIntensiveClassInput = z.infer<typeof individualIntensiveClassSchema>;

export const bulkImportIntensiveClassRowSchema = z.object({
  code: z.string().min(1, 'Kode PELITA wajib diisi'),
  name: z.string().optional().nullable(),
  attendancePct: z.number().min(0).max(100),
  attendedSessions: z.array(z.number().int().min(1).max(50)).optional().nullable(),
  sessionScores: z.array(intensiveSessionScoreSchema).optional().nullable(),
  preTestScore: z.number().min(0).max(100).optional().nullable(),
  postTestScore: z.number().min(0).max(100).optional().nullable(),
  status: z.enum(INTENSIVE_CLASS_STATUSES).optional(),
  notes: z.string().optional().nullable(),
});
export type BulkImportIntensiveClassRow = z.infer<typeof bulkImportIntensiveClassRowSchema>;

